import { useQuery } from "@tanstack/react-query";
import React from "react";

import * as fbs from "@/api/fbs";
import * as settingsApi from "@/api/system_settings";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadState } from "@/lib/scan-ui/screen";
import { LabelTemplatesCard } from "@/pages/settings/cards/LabelTemplatesCard";
import { PrinterCard } from "@/pages/settings/cards/PrinterCard";
import { SettingsPageHeader } from "@/pages/settings/SettingsPageHeader";

/** «Печать» — всё, что печатает склад: агенты, принтеры разделов и макеты.
 *
 * Принтеры жили на странице ФБС, хотя печатают паллетный лист, этикетку
 * приёмки и короба разные разделы в разных помещениях: искать настройку
 * принтера приёмки в «ФБС» было неоткуда догадаться. */
export function PrintingSettingsPage(): React.ReactElement {
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <SettingsPageHeader
        title="Печать"
        subtitle="Relay-агенты склада, принтеры по назначениям и макеты этикеток."
      />
      <RelayAgentsCard />
      <PrinterCard />
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Макеты этикеток</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Сырой EZPL из GoLabel: правится здесь, а не в коде. */}
          <LabelTemplatesCard />
        </CardContent>
      </Card>
    </div>
  );
}

/** Состояние relay-агентов: без агента сервер в локальную сеть склада не
 *  видит, и любая печать молча никуда не уходит.
 *
 * ⚠️ Общий агент (`default`) — это `relay_connected` ответа без `point_id`.
 * Перечень `agents` приходит, только когда в инсталляции заведены точки
 * сборки: пустой список и отсутствие ключа — разные вещи, и выдумывать
 * «агентов нет» там, где их не спрашивали, нельзя.
 *
 * ⚠️ Через `default` печатают ТОЛЬКО принтеры разделов (`listPrinters`,
 * поле `configured`) — печать точек идёт через их именованных агентов. Пока
 * ни один принтер раздела не настроен, отсутствие связи у `default` ничего
 * не значит — агент просто не используется, и красный «не подключён» тут
 * вводит в заблуждение больше, чем помогает. */
function RelayAgentsCard(): React.ReactElement {
  const printStatus = useQuery({
    queryKey: ["fbs-print-status", null],
    queryFn: () => fbs.getPrintStatus(),
  });
  // Тот же ключ кэша, что у `PrinterCard`: одна и та же коллекция принтеров
  // разделов, второй копии в другом кэше не заводим.
  const printers = useQuery({
    queryKey: ["label-printers"],
    queryFn: settingsApi.listPrinters,
  });

  const isLoading = printStatus.isLoading || printers.isLoading;
  const error = printStatus.error ?? printers.error;
  const data = printStatus.data;
  const printerList = printers.data;

  if (isLoading || error || !data || !printerList) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Агенты печати</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <LoadState
            isLoading={isLoading}
            error={error}
            onRetry={() => {
              void printStatus.refetch();
              void printers.refetch();
            }}
            className="flex flex-col items-center gap-3 p-2 text-center"
          />
        </CardContent>
      </Card>
    );
  }

  const sectionPrinterConfigured = printerList.some((p) => p.configured);
  // Красный «не подключён» — только когда default реально должен работать
  // (есть настроенный принтер раздела). Иначе — нейтральная подпись, а не
  // тревога вхолостую.
  const defaultUnused = !sectionPrinterConfigured && !data.relay_connected;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Агенты печати</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <AgentBadge
          name="default (общий агент склада)"
          connected={data.relay_connected}
          neutral={defaultUnused}
          neutralLabel="не используется: принтеры разделов не настроены"
        />
        {(data.agents ?? []).map((agent) => (
          <AgentBadge key={agent.agent} name={agent.agent} connected={agent.connected} />
        ))}
        <p className="text-xs text-muted-foreground">
          Печать идёт через relay-агента: сервер не видит локальную сеть склада
          напрямую. Агент точки сборки настраивается в её карточке («ФБС» →
          «Wildberries»).
        </p>
      </CardContent>
    </Card>
  );
}

function AgentBadge({
  name,
  connected,
  neutral,
  neutralLabel,
}: {
  name: string;
  connected: boolean;
  neutral?: boolean;
  neutralLabel?: string;
}): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border p-2">
      <span className="min-w-0 truncate text-sm">{name}</span>
      <span
        className={
          neutral
            ? "shrink-0 rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground"
            : connected
              ? "shrink-0 rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800"
              : "shrink-0 rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-800"
        }
      >
        {neutral ? neutralLabel : connected ? "подключён" : "не подключён"}
      </span>
    </div>
  );
}
